import React from "react";
import { Box, Typography } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import { useThemeManager } from "../../hooks/useThemeManager";
import HeroContent from "./hero/HeroContent";
import HeroVisual from "./hero/HeroVisual";

const Hero: React.FC = () => {
  const { isDark } = useThemeManager();
  const mode = isDark ? "dark" : "light";

  return (
    <Box
      component="section"
      id="hero"
      aria-labelledby="hero-title"
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100dvh",
        boxSizing: "border-box",
        pt: { xs: 10, md: 14 },
        pb: { xs: 7, md: 9 },
        display: "flex",
        alignItems: "stretch",
        overflow: "hidden",
        background:
          "radial-gradient(circle at 50% 40%, #0c1a30 0%, #070d1a 50%, #040810 100%)",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.012) 0.5px, transparent 0.5px), linear-gradient(90deg, rgba(255,255,255,0.012) 0.5px, transparent 0.5px)",
          backgroundSize: "36px 36px",
          pointerEvents: "none",
        }}
      />

      <Box
        component="img"
        src={`/assets/hero/particles-${mode}.svg`}
        alt=""
        aria-hidden="true"
        width="1200"
        height="400"
        loading="eager"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "55%",
          objectFit: "cover",
          pointerEvents: "none",
          opacity: isDark ? 1 : 0.8,
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 40,
          background:
            "linear-gradient(180deg, rgba(7,13,26,0) 0%, rgba(7,13,26,0.85) 100%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          px: { xs: 2, sm: 3, md: 5, lg: 6, xl: 8 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: {
            xs: "calc(100dvh - 136px)",
            md: "calc(100dvh - 184px)",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(320px, 0.95fr) minmax(700px, 1.5fr)",
            },
            alignItems: "center",
            columnGap: { xs: 0, md: 6, lg: 8 },
            rowGap: { xs: 4, md: 6 },
            flex: 1,
          }}
        >
          <Box>
            <HeroContent />
          </Box>
          <Box>
            <HeroVisual />
          </Box>
        </Box>

        <MotionBox
          component="div"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          sx={{
            mt: { xs: 5, md: 8 },
            textAlign: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              mb: 2,
              fontSize: "0.7rem",
              fontWeight: 500,
              color: "text.secondary",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              opacity: 0.7,
            }}
          >
            TRUSTED BY
          </Typography>
          <Box
            component="ul"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 2.5, md: 5 },
              listStyle: "none",
              p: 0,
              m: 0,
              color: "#94a3b8",
            }}
          >
            <Typography
              component="li"
              sx={{ listStyle: "none", fontSize: "0.8125rem", fontWeight: 700 }}
            >
              Amband
            </Typography>
            <Typography
              component="li"
              sx={{ listStyle: "none", fontSize: "0.8125rem", fontWeight: 600 }}
            >
              OneWorld Analytics
            </Typography>
            <Typography
              component="li"
              sx={{ listStyle: "none", fontSize: "0.8125rem", fontWeight: 500 }}
            >
              TierraViva AI
            </Typography>
            <Typography
              component="li"
              sx={{
                listStyle: "none",
                fontSize: "0.75rem",
                color: "#475569",
                opacity: 0.6,
              }}
            >
              and more
            </Typography>
          </Box>
        </MotionBox>
      </Box>
    </Box>
  );
};

export default Hero;
